import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, LogIn, UserPlus, ArrowRight, Shield, Sparkles, Smartphone, Check, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import ForceRefreshButton from '../components/ForceRefreshButton';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import PhotoCapture from '../components/PhotoCapture';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Unified entry page — shown at both / and /admin.
   Three options: Sign in · Create account · Continue as guest. */
export default function AuthGatePage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin'); // signin | signup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  // Tier-B departmental code chosen on signup. Optional but recommended.
  // Admins can later add more departments via the Users panel.
  const [department, setDepartment] = useState('');
  // Face-shot URL captured during sign-up. Mandatory before the form can
  // be submitted — powers the public Team Directory and the in-app avatar
  // pill from the very first sign-in.
  const [signupPhotoUrl, setSignupPhotoUrl] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const [staleVersion, setStaleVersion] = useState(null); // {current, server}
  const [forgotOpen, setForgotOpen] = useState(false);

  // Post-auth landing rule (Andres' Feb-11 update): EVERYONE lands on
  // `/app` after a successful sign-in / sign-up, including admins. The
  // public landing page is the canonical home — admins reach the
  // dashboard explicitly via the "Admin Panel" header button on /app.
  // The only auto-redirect to /admin/dashboard is when a viewer / guest
  // tries to enter that URL directly (handled by AdminPage hydration).
  useEffect(() => {
    const t = localStorage.getItem('admin_token');
    const guest = sessionStorage.getItem('atrium_guest');
    if (!t) {
      if (guest === '1') navigate('/app', { replace: true });
      return;
    }
    let cancelled = false;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.role) {
          try { localStorage.setItem('user_role', data.role); } catch (e) { /* ignore */ }
        }
        try { sessionStorage.removeItem('atrium_guest'); } catch (e) { /* ignore */ }
        navigate('/app', { replace: true });
      })
      .catch(() => {
        // Token rejected (expired / revoked). Wipe stale credentials so
        // the user can sign in fresh; stay on the gate.
        try {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('user_role');
          localStorage.removeItem('user_email');
        } catch (e) { /* ignore */ }
      });
    return () => { cancelled = true; };
  }, [navigate]);

  // Surface a friendly notice if we landed here from the idle-logout watchdog.
  // Cleared after one render so the message doesn't stick on subsequent visits.
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('atrium_signout_reason');
      if (reason === 'idle') {
        setInfo('You were signed out after 15 minutes of inactivity. Sign in again to continue.');
        sessionStorage.removeItem('atrium_signout_reason');
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Stale-cache detection — compare the last version we saw with what the server
  // reports now. If different, we surface a refresh banner above the form.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/app/version`).then(({ data }) => {
      if (cancelled) return;
      const serverVersion = data?.version;
      if (!serverVersion) return;
      const seen = localStorage.getItem('atrium_seen_version');
      if (seen && seen !== serverVersion) {
        setStaleVersion({ current: seen, server: serverVersion });
      } else if (!seen) {
        localStorage.setItem('atrium_seen_version', serverVersion);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const goAfterAuth = (data) => {
    // Persist token + role. Per Andres (Feb 11): EVERYONE — viewer,
    // editor, engineer, admin — lands on /app after a successful auth.
    // The public landing is the canonical home; admins jump to the
    // dashboard via the in-page "Admin Panel" button when they need it.
    localStorage.setItem('admin_token', data.token);
    localStorage.setItem('user_email', data.email);
    if (data.role) {
      localStorage.setItem('user_role', data.role);
    }
    // Tell the rest of the app that auth state changed so anything that
    // hangs off the token (notably usePermissions / canAccess gating)
    // re-fetches against the new credentials immediately rather than
    // showing a flash of "guest" state.
    try { window.dispatchEvent(new CustomEvent('atrium:auth-changed')); } catch (e) { /* ignore */ }
    // Clear any guest session flag so a previous "Continue as guest" tap
    // doesn't keep bouncing the now-signed-in user around.
    try { sessionStorage.removeItem('atrium_guest'); } catch (e) { /* ignore */ }
    navigate('/app', { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setInfo(''); setBusy(true);
    try {
      if (mode === 'signin') {
        const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password }, { withCredentials: true });
        goAfterAuth(data);
      } else {
        // Sign-up validation: enforce a re-entered password to catch typos
        // before the new account is locked behind a wrong-but-confirmed
        // password the user can't remember the exact casing of.
        // Strict email validation — every account MUST have a valid
        // email address (the admin uses the existing seeded `andresmocny`
        // username, but new accounts go through registration only).
        const trimmedEmail = email.trim();
        const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!EMAIL_RE.test(trimmedEmail)) {
          setErr('Please enter a valid email address (e.g. you@property.com). Email is required to create an account.');
          return;
        }
        const policyErr = passwordPolicyError(password);
        if (policyErr) {
          setErr(policyErr);
          return;
        }
        if (password !== passwordConfirm) {
          setErr("Passwords don't match — please re-type the same password in both fields.");
          return;
        }
        // Mandatory face shot — powers the public Team Directory and
        // the in-app avatar pill from the very first sign-in.
        if (!signupPhotoUrl) {
          setErr('Please take a face shot before creating your account — we use it for the team directory.');
          return;
        }
        await axios.post(`${API}/auth/register`, {
          email: trimmedEmail,
          password,
          name,
          photo_url: signupPhotoUrl,
          department,
        });
        setInfo('Account created! Signing you in…');
        const { data } = await axios.post(`${API}/auth/login`, { email: trimmedEmail, password }, { withCredentials: true });
        setTimeout(() => goAfterAuth(data), 400);
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const continueAsGuest = () => {
    sessionStorage.setItem('atrium_guest', '1');
    navigate('/app', { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{
        background:
          'radial-gradient(circle at 20% 10%, rgba(0,61,165,0.18), transparent 50%), radial-gradient(circle at 80% 90%, rgba(31,79,190,0.18), transparent 55%), linear-gradient(135deg, #F4F1EB 0%, #FFFFFF 100%)',
      }}
      data-testid="auth-gate-page"
    >
      {/* Subtle grain */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, #003DA5 0 1px, transparent 1px 4px)' }} />

      <div className="relative w-full max-w-5xl grid md:grid-cols-2 gap-6 items-stretch">
        {/* Brand pane */}
        <div
          className="hidden md:flex flex-col justify-between rounded-3xl p-10 text-white relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #003DA5 0%, #0046C0 55%, #1F4FBE 100%)' }}
        >
          <div>
            <img src="/logo-hyatt-inclusive.jpg" alt="World of Hyatt · Inclusive Collection"
              className="h-12 w-auto rounded-md p-1.5 bg-white inline-block" />
            <h1
              className="text-4xl sm:text-5xl font-bold mt-8 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="auth-hero-title"
            >
              Hotel Management Deck
            </h1>
            <p
              className="mt-4 text-sm leading-relaxed opacity-90"
              style={{ fontFamily: 'Manrope' }}
              data-testid="auth-hero-description"
            >
              A comprehensive tool for Asset Managers, VPs, ownership, and
              multi-property leaders to manage and control the entire hotel
              operation from a single screen.
            </p>
            <p
              className="mt-4 text-base italic"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#A3D4D4' }}
              data-testid="auth-hero-tagline"
            >
              The Rebirth of Dreams Rose Hall<sup>®</sup> Resort &amp; Spa
            </p>
          </div>
          <div className="space-y-2 mt-10 text-xs" style={{ fontFamily: 'Manrope' }}>
            <div className="flex items-center gap-2 opacity-95"><Sparkles className="w-3.5 h-3.5" /> 86 workspace areas · 30 milestones</div>
            <div className="flex items-center gap-2 opacity-95"><Shield className="w-3.5 h-3.5" /> Confidential branches require admin sign-in</div>
          </div>
          <div aria-hidden className="absolute -bottom-12 -right-12 w-72 h-72 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.18), transparent 60%)' }} />
        </div>

        {/* Form pane */}
        <div className="rounded-3xl p-7 sm:p-9" style={{ backgroundColor: 'white', boxShadow: '0 20px 60px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,61,165,0.06)' }}>
          <div className="flex items-center gap-3 mb-1">
            <div className="md:hidden">
              <img src="/logo-hyatt-inclusive.jpg" alt="WoH Inclusive Collection" className="h-9 rounded p-1 bg-blue-50" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#003DA5', fontFamily: "'Cormorant Garamond', serif" }}>
              {mode === 'signin' ? 'Welcome back' : 'Create your account'}
            </h2>
          </div>
          <p className="text-xs sm:text-sm mb-5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
            {mode === 'signin'
              ? 'Sign in with your email and password to access the reconstruction workspace.'
              : 'New here? Create a free viewer account in 10 seconds.'}
          </p>

          {/* Stale version banner — nudges users to refresh if their cached SW is older than what the server reports */}
          {staleVersion && (
            <button
              type="button"
              data-testid="stale-version-banner"
              onClick={() => {
                localStorage.setItem('atrium_seen_version', staleVersion.server);
                // Trigger the same cache-purge path as ForceRefreshButton
                (async () => {
                  try {
                    if (navigator.serviceWorker) {
                      const regs = await navigator.serviceWorker.getRegistrations();
                      for (const r of regs) await r.unregister();
                    }
                    if (window.caches) {
                      const keys = await caches.keys();
                      for (const k of keys) await caches.delete(k);
                    }
                  } catch (e) { /* ignore */ }
                  window.location.reload();
                })();
              }}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 mb-3 rounded-lg text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                border: '1px solid #FCD34D',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              <span className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Newer version available
              </span>
              <span className="underline">Refresh now →</span>
            </button>
          )}

          {/* Mode toggle */}
          <div className="flex gap-1 p-1 rounded-full mb-5" style={{ backgroundColor: '#F0F4FA' }}>
            <ModeTab active={mode === 'signin'} label="Sign in" icon={<LogIn className="w-3.5 h-3.5" />} onClick={() => setMode('signin')} testid="auth-tab-signin" />
            <ModeTab active={mode === 'signup'} label="Create account" icon={<UserPlus className="w-3.5 h-3.5" />} onClick={() => setMode('signup')} testid="auth-tab-signup" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid={`auth-form-${mode}`}>
            {mode === 'signup' && (
              <SignupFaceShotBlock
                photoUrl={signupPhotoUrl}
                onChange={setSignupPhotoUrl}
                initials={(name || email || '?').trim().slice(0, 2).toUpperCase()}
              />
            )}
            {mode === 'signup' && (
              <Field label="Full name (optional)">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe"
                  data-testid="auth-name"
                  className="w-full px-3 py-2.5 rounded-lg border outline-none focus:ring-2"
                  style={{ borderColor: '#D9E3F1', fontFamily: 'Manrope', fontSize: 14 }} />
              </Field>
            )}
            {mode === 'signup' && (
              <Field label="Department (optional)">
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  data-testid="auth-department"
                  className="w-full px-3 py-2.5 rounded-lg border outline-none focus:ring-2 bg-white"
                  style={{ borderColor: '#D9E3F1', fontFamily: 'Manrope', fontSize: 14 }}
                >
                  <option value="">Select your department (you can ask admins for access to others later)</option>
                  <option value="spa">Spa</option>
                  <option value="fb">F&amp;B</option>
                  <option value="rooms">Rooms</option>
                  <option value="finance">Finance</option>
                  <option value="sales">Sales</option>
                  <option value="engineering">Engineering</option>
                  <option value="entertainment">Entertainment</option>
                  <option value="water-sports">Water Sports</option>
                  <option value="hr">HR</option>
                  <option value="it">IT (requires admin approval)</option>
                  <option value="security">Security</option>
                  <option value="contractor">Contractor</option>
                  <option value="procurement">Procurement</option>
                </select>
              </Field>
            )}
            <Field label={mode === 'signup' ? 'Email address (required)' : 'Email or username'}>
              <input
                value={email} onChange={(e) => setEmail(e.target.value)} required
                type={mode === 'signup' ? 'email' : 'text'}
                autoComplete={mode === 'signup' ? 'email' : 'username'}
                data-testid="auth-email"
                placeholder="you@property.com"
                className="w-full px-3 py-2.5 rounded-lg border outline-none focus:ring-2"
                style={{ borderColor: '#D9E3F1', fontFamily: 'Manrope', fontSize: 14 }}
              />
              {mode === 'signup' && (
                <p className="text-[10px] mt-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  We use your email address to send permission updates and password recovery links.
                </p>
              )}
            </Field>
            <Field label="Password">
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  data-testid="auth-password"
                  placeholder={mode === 'signin' ? '••••••••' : 'At least 10 characters · upper + lower + number'}
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border outline-none focus:ring-2"
                  style={{ borderColor: '#D9E3F1', fontFamily: 'Manrope', fontSize: 14 }}
                />
                <button type="button" onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-gray-500 hover:text-gray-800"
                  aria-label="Toggle password visibility">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === 'signup' && password && (
                <PasswordStrengthMeter password={password} />
              )}
            </Field>

            {/* Re-enter password — signup only. Catches typos before the
                user gets locked out of their fresh account behind a
                wrong-but-confirmed password they can't remember. Live
                visual feedback (green check / amber dot) reduces the
                cognitive load of comparing two long strings. */}
            {mode === 'signup' && (
              <Field label="Re-enter password">
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                    data-testid="auth-password-confirm"
                    placeholder="Type the same password again"
                    className="w-full px-3 py-2.5 pr-10 rounded-lg border outline-none focus:ring-2"
                    style={{
                      borderColor: passwordConfirm
                        ? (passwordConfirm === password ? '#22C55E' : '#F59E0B')
                        : '#D9E3F1',
                      fontFamily: 'Manrope',
                      fontSize: 14,
                    }}
                  />
                  {passwordConfirm && passwordConfirm === password && (
                    <Check
                      data-testid="auth-password-match-ok"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4"
                      style={{ color: '#22C55E' }}
                    />
                  )}
                </div>
                {passwordConfirm && passwordConfirm !== password && (
                  <p className="text-[10px] mt-1" style={{ color: '#92400E', fontFamily: 'Manrope' }} data-testid="auth-password-mismatch">
                    Passwords don't match yet
                  </p>
                )}
              </Field>
            )}
            {mode === 'signin' && (
              <div className="-mt-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  data-testid="auth-forgot-password"
                  className="text-[11px] font-bold underline-offset-2 hover:underline transition-all"
                  style={{ color: '#003DA5', fontFamily: 'Manrope' }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {err && <div className="text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEF2F2', color: '#B91C1C', fontFamily: 'Manrope' }} data-testid="auth-error">{err}</div>}
            {info && <div className="text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: '#ECFDF5', color: '#047857', fontFamily: 'Manrope' }}>{info}</div>}

            <button
              type="submit" disabled={busy}
              data-testid="auth-submit"
              className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
              style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(0,61,165,0.3)' }}
            >
              {busy ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Create account & sign in'}
              {!busy && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          {/* Divider + Guest */}
          <div className="flex items-center gap-3 my-5" aria-hidden>
            <div className="flex-1 h-px" style={{ backgroundColor: '#E5EBF4' }} />
            <span className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: '#E5EBF4' }} />
          </div>

          <button
            type="button"
            onClick={continueAsGuest}
            data-testid="auth-guest"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: 'white', color: '#003DA5', border: '1.5px solid #A3C4EE', fontFamily: 'Manrope' }}
          >
            Continue as guest <span className="text-[10px] opacity-70">(view only)</span>
          </button>
          <p className="text-[10px] text-center mt-2 opacity-70" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
            Guests can view the property map, technical plans and public roadmaps. Locked branches and forms remain private.
          </p>

          {/* QR code — desktop users can scan with their phone to open the
              app on mobile without typing the URL. Defaults to the canonical
              public URL (REACT_APP_PUBLIC_URL) and falls back to the current
              origin so it Just Works in dev too. */}
          <MobileQrCard />

          {/* Clear Cache & Reload — gives users a one-click way to grab the latest PWA build */}
          <div className="mt-5 pt-4 border-t flex flex-col items-center gap-1.5" style={{ borderColor: '#E5EBF4' }}>
            <ForceRefreshButton variant="pill" label="Clear Cache & Reload" className="text-[11px] px-4 py-2" />
            <p className="text-[10px] text-center opacity-70" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              Not seeing the latest version? Click to force-refresh.
            </p>
          </div>
        </div>
      </div>

      {/* Forgot password modal — mounted at the page root so it floats
          above the Auth Gate's split-pane layout. */}
      <ForgotPasswordModal
        open={forgotOpen}
        defaultEmail={email}
        onClose={() => setForgotOpen(false)}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold tracking-[0.15em] uppercase mb-1" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>{label}</span>
      {children}
    </label>
  );
}

// Password policy: 10+ chars, ≥1 uppercase, ≥1 lowercase, ≥1 digit. Mirrors
// the backend `_password_policy_violation()` helper so the form blocks
// submission BEFORE round-tripping to the server. Returns the human-readable
// error string, or empty string when the password is fine.
function passwordPolicyError(pw) {
  if (!pw || typeof pw !== 'string') return 'Password is required.';
  if (pw.length < 10) return 'Password must be at least 10 characters.';
  const missing = [];
  if (!/[A-Z]/.test(pw)) missing.push('an uppercase letter');
  if (!/[a-z]/.test(pw)) missing.push('a lowercase letter');
  if (!/[0-9]/.test(pw)) missing.push('a number');
  if (missing.length) return 'Password must contain ' + missing.join(', ') + '.';
  return '';
}

// Live, four-rule strength meter that flips a row of pills from amber to
// green as the user types. Avoids the doom-loop of hitting Submit only to
// get bounced by a server-side policy check the user couldn't preview.
function PasswordStrengthMeter({ password }) {
  const rules = [
    { id: 'length', label: '10+ characters', ok: (password || '').length >= 10 },
    { id: 'upper',  label: 'Uppercase A-Z', ok: /[A-Z]/.test(password || '') },
    { id: 'lower',  label: 'Lowercase a-z', ok: /[a-z]/.test(password || '') },
    { id: 'digit',  label: 'Number 0-9',    ok: /[0-9]/.test(password || '') },
  ];
  const passed = rules.filter((r) => r.ok).length;
  const allGood = passed === rules.length;
  return (
    <div data-testid="auth-password-strength" className="mt-1.5">
      <div className="flex items-center gap-1 mb-1">
        {rules.map((r, i) => (
          <span
            key={r.id}
            data-testid={`pw-rule-bar-${r.id}`}
            className="h-1 flex-1 rounded-full transition-all"
            style={{ backgroundColor: r.ok ? '#22C55E' : (i < passed ? '#22C55E' : '#E5E7EB') }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rules.map((r) => (
          <span
            key={r.id}
            data-testid={`pw-rule-${r.id}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
            style={{
              backgroundColor: r.ok ? '#DCFCE7' : '#FEF3C7',
              color: r.ok ? '#15803D' : '#92400E',
              fontFamily: 'Manrope',
            }}
          >
            {r.ok ? '✓' : '•'} {r.label}
          </span>
        ))}
      </div>
      {allGood && (
        <p
          data-testid="pw-rule-all-good"
          className="text-[10px] mt-1 font-bold"
          style={{ color: '#15803D', fontFamily: 'Manrope' }}
        >
          ✓ Strong password — you're good to go
        </p>
      )}
    </div>
  );
}

// Mandatory face-shot block at the top of the sign-up form.
// Renders the same prominent "Step 1 of 2" treatment as the admin
// EditUserDialog so brand-new self-registered users still seed the
// public Team Directory with a real photo from day zero.
function SignupFaceShotBlock({ photoUrl, onChange, initials }) {
  const captured = !!photoUrl;
  return (
    <div
      data-testid="signup-faceshot-block"
      className="rounded-xl p-4 mb-1"
      style={{
        background: 'linear-gradient(135deg, rgba(0,61,165,0.06) 0%, rgba(31,79,190,0.10) 100%)',
        border: '1px dashed #A3C4EE',
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.15em] uppercase"
          style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}
        >
          Step 1 · Take a face shot
        </span>
        <span
          data-testid={captured ? 'signup-faceshot-ok' : 'signup-faceshot-warning'}
          className="text-[10px] font-bold flex items-center gap-1"
          style={{
            color: captured ? '#047857' : '#92400E',
            fontFamily: 'Manrope',
          }}
        >
          {captured ? '✓ Face shot captured' : '⚠ Required'}
        </span>
      </div>
      <p className="text-[11px] mb-3 leading-snug" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
        Your photo will appear on the public team directory and on your profile pill in the workspace.
        Use your camera or upload a clear front-facing photo.
      </p>
      <div className="flex justify-center">
        <PhotoCapture
          value={photoUrl}
          onChange={onChange}
          token={null}
          signupMode
          prominent
          accent="#003DA5"
          size={108}
          initials={initials || '?'}
          testIdPrefix="signup-photo"
        />
      </div>
    </div>
  );
}

function ModeTab({ active, label, icon, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all"
      style={{
        backgroundColor: active ? '#003DA5' : 'transparent',
        color: active ? 'white' : '#4A5D5D',
        fontFamily: 'Manrope',
      }}
    >
      {icon} {label}
    </button>
  );
}

// Compact "Open on mobile" card with a scannable QR code + tap-to-copy URL.
// The QR encodes the canonical public URL (env override) or the current origin
// so dev environments work too. Card is collapsible to keep the gate uncluttered.
function MobileQrCard() {
  const url = useMemo(() => {
    const envUrl = process.env.REACT_APP_PUBLIC_URL;
    if (envUrl) return envUrl.replace(/\/$/, '') + '/app';
    if (typeof window !== 'undefined') return `${window.location.origin}/app`;
    return '';
  }, []);
  const [open, setOpen] = useState(true); // open by default so users see the QR immediately
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard not available — silently no-op */ }
  };

  return (
    <div className="mt-5 pt-4 border-t" style={{ borderColor: '#E5EBF4' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="qr-toggle"
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all"
        style={{ backgroundColor: '#F0F4FA', color: '#003DA5', fontFamily: 'Manrope' }}
      >
        <Smartphone className="w-3.5 h-3.5" />
        Open on mobile · scan QR
        <ArrowRight
          className="w-3 h-3 transition-transform"
          style={{ transform: open ? 'rotate(90deg)' : 'none' }}
        />
      </button>
      {open && (
        <div
          data-testid="qr-card"
          className="mt-3 p-4 rounded-2xl flex flex-col items-center gap-3"
          style={{ backgroundColor: 'white', boxShadow: '0 2px 14px rgba(0,61,165,0.08)', border: '1px solid #E5EBF4' }}
        >
          <div className="p-3 rounded-xl" style={{ backgroundColor: 'white', border: '2px solid #003DA5' }}>
            <QRCodeSVG
              value={url}
              size={172}
              bgColor="#ffffff"
              fgColor="#003DA5"
              level="M"
              includeMargin={false}
            />
          </div>
          <p
            className="text-[10px] text-center max-w-[220px] leading-relaxed"
            style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
          >
            Open the camera app on your phone and point it at the code — your phone will offer to launch this site.
          </p>
          <button
            type="button"
            onClick={onCopy}
            data-testid="qr-copy-url"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono transition-all"
            style={{
              backgroundColor: copied ? '#DCFCE7' : '#F0F4FA',
              color: copied ? '#166534' : '#003DA5',
              fontFamily: 'monospace',
              maxWidth: '100%',
            }}
            title="Copy URL"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            <span className="truncate">{copied ? 'Copied!' : url}</span>
          </button>
        </div>
      )}
    </div>
  );
}
